import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { footballApi, fantasyApi, transferApi } from '../api/endpoints'
import type { FootballMatch, MatchPrediction, FantasyPlayer, FantasyTeam, FantasyTeamCreateDTO, CoinTransfer, FootballLeague, UserFootballScore, DreamTeam, DreamTeamCreateDTO, RaffleWinner, FootballGameMode } from '../types'

export function useUpcomingMatches(limit = 20, mode?: FootballGameMode) {
  return useQuery({
    queryKey: ['football', 'upcoming', limit, mode ?? 'all'],
    queryFn: () => footballApi.getUpcomingMatches(limit, mode),
  })
}

export function useMatch(id: number) {
  return useQuery({
    queryKey: ['football', 'match', id],
    queryFn: () => footballApi.getMatch(id),
    enabled: !!id,
  })
}

export function useSubmitPrediction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ matchId, choice }: { matchId: number; choice: 'home' | 'away' | 'draw' }) =>
      footballApi.submitPrediction(matchId, choice),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'my-predictions'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'my-predictions'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'match'] })
    },
  })
}

export function useMyPredictions() {
  return useQuery({
    queryKey: ['football', 'my-predictions'],
    queryFn: () => footballApi.getMyPredictions(),
  })
}

export function useMatchPredictions(matchId: number) {
  return useQuery({
    queryKey: ['football', 'predictions', matchId],
    queryFn: () => footballApi.getMatchPredictions(matchId),
    enabled: !!matchId,
  })
}

export function useFantasyPlayers() {
  return useQuery({
    queryKey: ['fantasy', 'players'],
    queryFn: () => fantasyApi.getAllPlayers(),
  })
}

export function useMatchFantasyPlayers(matchId: number) {
  return useQuery({
    queryKey: ['fantasy', 'match-players', matchId],
    queryFn: () => fantasyApi.getMatchPlayers(matchId),
    enabled: !!matchId,
  })
}

export function useFantasyPlayersByPosition(position: string) {
  return useQuery({
    queryKey: ['fantasy', 'players', position],
    queryFn: () => fantasyApi.getPlayersByPosition(position),
    enabled: !!position,
  })
}

export function useFormations() {
  return useQuery({
    queryKey: ['fantasy', 'formations'],
    queryFn: () => fantasyApi.getFormations(),
  })
}

export function useCreateFantasyTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: FantasyTeamCreateDTO) => fantasyApi.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'my-team'] })
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'match-teams'] })
    },
  })
}

export function useUpdateFantasyTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, ...data }: FantasyTeamCreateDTO & { teamId: number }) =>
      fantasyApi.updateTeam(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'my-team'] })
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'match-players'] })
    },
  })
}

export function useFantasyTeam(id: number) {
  return useQuery({
    queryKey: ['fantasy', 'team', id],
    queryFn: () => fantasyApi.getTeam(id),
    enabled: !!id,
  })
}

export function useFantasyTeamByShareToken(token: string) {
  return useQuery({
    queryKey: ['fantasy', 'team-share', token],
    queryFn: () => fantasyApi.getTeamByShareToken(token),
    enabled: !!token,
  })
}

export function useExploreTeams(query: string, limit: number = 30) {
  return useQuery({
    queryKey: ['fantasy', 'explore', query, limit],
    queryFn: () => fantasyApi.exploreTeams(query, limit),
    placeholderData: (prev) => prev,
  })
}

export function useExploreDreamTeams(query: string, limit: number = 30) {
  return useQuery({
    queryKey: ['football', 'dream-explore', query, limit],
    queryFn: () => footballApi.exploreDreamTeams(query, limit),
    placeholderData: (prev) => prev,
  })
}

export function useFantasyTeamWithPlayers(id: number) {
  return useQuery({
    queryKey: ['fantasy', 'team-players', id],
    queryFn: () => fantasyApi.getTeamWithPlayers(id),
    enabled: !!id,
  })
}

export function useMyTeamForMatch(matchId: number) {
  return useQuery({
    queryKey: ['fantasy', 'my-team', matchId],
    queryFn: () => fantasyApi.getMyTeamForMatch(matchId),
    enabled: !!matchId,
  })
}

export function useMatchTeams(matchId: number) {
  return useQuery({
    queryKey: ['fantasy', 'match-teams', matchId],
    queryFn: () => fantasyApi.getMatchTeams(matchId),
    enabled: !!matchId,
  })
}

export function useSubmitFantasyTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (teamId: number) => fantasyApi.submitTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'my-team'] })
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'match-teams'] })
    },
  })
}

export function useVote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, rating }: { teamId: number; rating: number }) => fantasyApi.vote(teamId, rating),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'match-teams'] })
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'top-teams'] })
      queryClient.invalidateQueries({ queryKey: ['fantasy', 'my-vote'] })
    },
  })
}

export function useTopTeams(matchId: number, limit = 5) {
  return useQuery({
    queryKey: ['fantasy', 'top-teams', matchId, limit],
    queryFn: () => fantasyApi.getTopTeams(matchId, limit),
    enabled: !!matchId,
  })
}

export function useMyVote(teamId: number) {
  return useQuery({
    queryKey: ['fantasy', 'my-vote', teamId],
    queryFn: () => fantasyApi.getMyVote(teamId),
    enabled: !!teamId,
  })
}

export function useSetPIN() {
  return useMutation({
    mutationFn: (pin: string) => transferApi.setPIN(pin),
  })
}

export function useVerifyPIN() {
  return useMutation({
    mutationFn: (pin: string) => transferApi.verifyPIN(pin),
  })
}

export function useHasPIN() {
  return useQuery({
    queryKey: ['transfers', 'has-pin'],
    queryFn: () => transferApi.hasPIN(),
  })
}

export function useTransferToUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ recipient, coinType, amount, pin }: { recipient: string; coinType: 'bronze' | 'silver' | 'gold' | 'diamond'; amount: number; pin: string }) =>
      transferApi.transferToUser(recipient, coinType, amount, pin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transfers'] })
    },
  })
}

export function useRequestAdminTransfer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ coinType, amount, walletAddress, pin }: { coinType: 'bronze' | 'silver' | 'gold' | 'diamond'; amount: number; walletAddress: string; pin: string }) =>
      transferApi.requestAdminTransfer(coinType, amount, walletAddress, pin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transfers'] })
    },
  })
}

export function useTransfers(limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['transfers', 'list', limit, offset],
    queryFn: () => transferApi.getTransfers(limit, offset),
  })
}

// Football game modes: the football section holds both league and big match
export function useActiveLeague(mode: FootballGameMode) {
  return useQuery({
    queryKey: ['football', 'league', 'active', mode],
    queryFn: () => footballApi.getActiveLeague(mode),
    enabled: !!mode,
  })
}

export function useUserFootballScore(leagueId: number) {
  return useQuery({
    queryKey: ['football', 'league', leagueId, 'score'],
    queryFn: () => footballApi.getUserScore(leagueId),
    enabled: !!leagueId,
  })
}

export function useLeagueLeaderboard(leagueId: number, limit = 10) {
  return useQuery({
    queryKey: ['football', 'league', leagueId, 'leaderboard', limit],
    queryFn: () => footballApi.getLeaderboard(leagueId, limit),
    enabled: !!leagueId,
  })
}

export function useLeagueMonthlyLeaderboard(leagueId: number, limit = 10) {
  return useQuery({
    queryKey: ['football', 'league', leagueId, 'monthly-leaderboard', limit],
    queryFn: () => footballApi.getMonthlyLeaderboard(leagueId, limit),
    enabled: !!leagueId,
  })
}

export function useUserRaffleWinnings(leagueId: number) {
  return useQuery({
    queryKey: ['football', 'league', leagueId, 'winnings'],
    queryFn: () => footballApi.getUserWinnings(leagueId),
    enabled: !!leagueId,
  })
}

export function useMyLeaguePredictions() {
  return useQuery({
    queryKey: ['football', 'league', 'my-predictions'],
    queryFn: () => footballApi.getMyLeaguePredictions(),
  })
}

export function useSubmitLeaguePrediction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ matchId, choices }: { matchId: number; choices: Array<'home' | 'away' | 'draw'> }) =>
      footballApi.submitLeaguePrediction(matchId, choices),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'score'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'leaderboard'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'my-predictions'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'matches'] })
    },
  })
}

export function useSubmitLeaguePredictionsBatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      predictions: Array<{ match_id: number; choices: Array<'home' | 'away' | 'draw'> }>,
    ) => footballApi.submitLeaguePredictionsBatch(predictions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'score'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'leaderboard'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'my-predictions'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'matches'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'upcoming'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'day-goals'] })
    },
  })
}

export function useLeagueDayGoalsStatus(leagueId: number) {
  return useQuery({
    queryKey: ['football', 'league', 'day-goals', leagueId],
    queryFn: () => footballApi.getDayGoalsStatus(leagueId),
    enabled: !!leagueId,
  })
}

export function useSubmitLeagueDayGoals() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      leagueId,
      matchDate,
      homeTotal,
      awayTotal,
      payment,
    }: {
      leagueId: number
      matchDate: string
      homeTotal: number
      awayTotal: number
      payment: 'bronze' | 'ads'
    }) =>
      footballApi.submitDayGoals(leagueId, {
        match_date: matchDate,
        predicted_home_total: homeTotal,
        predicted_away_total: awayTotal,
        payment,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'day-goals'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'winnings'] })
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
    },
  })
}

export function useMyLeagueScores() {
  return useQuery({
    queryKey: ['football', 'league', 'my-scores'],
    queryFn: () => footballApi.getMyScores(),
  })
}

export function useMyRaffleWinnings() {
  return useQuery({
    queryKey: ['football', 'league', 'my-winnings'],
    queryFn: () => footballApi.getMyWinnings(),
  })
}

export function useCreateDreamTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: DreamTeamCreateDTO) => footballApi.createDreamTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'dream-team'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'match-dream-teams'] })
    },
  })
}

export function useDreamTeam(matchId: number) {
  return useQuery({
    queryKey: ['football', 'league', 'dream-team', matchId],
    queryFn: () => footballApi.getDreamTeam(matchId),
    enabled: !!matchId,
    retry: false,
  })
}

export function useDreamTeamById(id: number) {
  return useQuery({
    queryKey: ['football', 'league', 'dream-team-by-id', id],
    queryFn: () => footballApi.getDreamTeamById(id),
    enabled: !!id,
  })
}

export function useVoteDreamTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, rating }: { teamId: number; rating: number }) =>
      footballApi.voteDreamTeam(teamId, rating),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'dream-team-by-id', vars.teamId] })
      queryClient.invalidateQueries({ queryKey: ['football', 'dream-team-my-vote', vars.teamId] })
      queryClient.invalidateQueries({ queryKey: ['football', 'dream-explore'] })
    },
  })
}

export function useMyDreamTeamVote(teamId: number) {
  return useQuery({
    queryKey: ['football', 'dream-team-my-vote', teamId],
    queryFn: () => footballApi.getMyDreamTeamVote(teamId),
    enabled: !!teamId,
  })
}

export function useMatchDreamTeams(matchId: number) {
  return useQuery({
    queryKey: ['football', 'match-dream-teams', matchId],
    queryFn: () => footballApi.getMatchDreamTeams(matchId),
    enabled: !!matchId,
  })
}

export function useUpdateDreamTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, ...data }: DreamTeamCreateDTO & { teamId: number }) =>
      footballApi.updateDreamTeam(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'dream-team'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'match-dream-teams'] })
    },
  })
}

export function useSubmitDreamTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (teamId: number) => footballApi.submitDreamTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['football', 'league', 'dream-team'] })
      queryClient.invalidateQueries({ queryKey: ['football', 'match-dream-teams'] })
    },
  })
}